import { useEffect, useState } from "react";

import { api } from "../api/client";
import { useApiList, useApiOne, useMutation } from "../api/hooks";
import type { ServiceAreas } from "../api/types";
import { useToast } from "./ui";

/** The DTO lists are `serde(default)`, so optional in the schema; the form always holds all three. */
type Areas = Required<ServiceAreas>;
type Named = { id: string; name: string };

/** Where a vendor delivers parcels (02 §4.2). Nothing selected = everywhere. */
export function ServiceAreas({ vendorId }: { vendorId: string }) {
  const { data, reload } = useApiOne<ServiceAreas>("/vendor/service-areas", { vendor_id: vendorId });
  const countries = useApiList<Named & { iso2: string }>("/geo/countries", {});
  const [states, setStates] = useState<(Named & { country_id: string })[]>([]);
  const [cities, setCities] = useState<(Named & { state_id: string })[]>([]);
  const [draft, setDraft] = useState<Areas | null>(null);
  const { run, busy } = useMutation();
  const toast = useToast();

  useEffect(() => {
    if (data && !draft) setDraft({ country_ids: data.country_ids ?? [], state_ids: data.state_ids ?? [], city_ids: data.city_ids ?? [] });
  }, [data, draft]);

  useEffect(() => {
    let live = true;
    (async () => {
      const all: (Named & { country_id: string })[] = [];
      for (const c of countries.data) {
        const r = await api<(Named & { country_id: string })[]>(`/geo/countries/${c.id}/states`);
        all.push(...r.data);
      }
      if (live) setStates(all);
    })();
    return () => {
      live = false;
    };
  }, [countries.data]);

  useEffect(() => {
    let live = true;
    (async () => {
      const all: (Named & { state_id: string })[] = [];
      for (const s of states) {
        const r = await api<(Named & { state_id: string })[]>(`/geo/states/${s.id}/cities`);
        all.push(...r.data);
      }
      if (live) setCities(all);
    })();
    return () => {
      live = false;
    };
  }, [states]);

  if (!draft) return <p className="muted">Loading…</p>;
  const total = draft.country_ids.length + draft.state_ids.length + draft.city_ids.length;
  const pick = (key: keyof Areas) => (e: React.ChangeEvent<HTMLSelectElement>) =>
    setDraft({ ...draft, [key]: Array.from(e.target.selectedOptions, (o) => o.value) });

  async function save() {
    const r = await run<ServiceAreas>("/vendor/service-areas", { method: "PUT", query: { vendor_id: vendorId }, body: draft });
    if (r.ok) {
      toast("Service areas saved");
      setDraft({ country_ids: r.data.country_ids ?? [], state_ids: r.data.state_ids ?? [], city_ids: r.data.city_ids ?? [] });
      reload();
    } else toast(r.error, "err");
  }

  return (
    <>
      <h2>Parcel service areas</h2>
      <p className="muted">{total === 0 ? "No areas selected: this vendor serves everywhere." : "Parcel stops must fall in a selected city, state or country."}</p>
      <div className="form-row" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
        <label>
          Countries
          <select multiple className="input" size={5} value={draft.country_ids} onChange={pick("country_ids")}>
            {countries.data.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          States
          <select multiple className="input" size={5} value={draft.state_ids} onChange={pick("state_ids")}>
            {states.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Cities
          <select multiple className="input" size={5} value={draft.city_ids} onChange={pick("city_ids")}>
            {cities.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="form-actions">
        <button type="button" className="btn btn-ghost" onClick={() => setDraft({ country_ids: [], state_ids: [], city_ids: [] })}>
          Clear
        </button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={save}>
          {busy ? "Saving…" : "Save areas"}
        </button>
      </div>
    </>
  );
}
